import { useState, useEffect } from "react";
import {
  Modal,
  Checkbox,
  ActionIcon,
  Button,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import { IconArrowUp, IconArrowDown, IconGridDots } from "@tabler/icons-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ALL_INCOMING_COLUMNS,
  type IncomingQueuePreferences,
} from "../../pages/workflows/queueColumnConfig";
import { ColumnTooltip } from "./ColumnTooltip";

interface SortableRowProps {
  id: string;
  label: string;
  tooltip?: string;
  checked: boolean;
  index: number;
  total: number;
  animatingId: string | null;
  animatingDirection: "up" | "down" | null;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function SortableRow({
  id,
  label,
  tooltip,
  checked,
  index,
  total,
  animatingId,
  animatingDirection,
  onToggle,
  onMoveUp,
  onMoveDown,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const isAnimating = animatingId === id;
  const translateY = isAnimating ? (animatingDirection === "up" ? -36 : 36) : 0;

  const style = {
    transform: isDragging
      ? CSS.Transform.toString(transform)
      : `translateY(${translateY}px)`,
    transition: isDragging ? transition : "transform 180ms ease",
    opacity: isDragging ? 0.4 : 1,
    background: isDragging ? "var(--mantine-color-blue-0)" : undefined,
    borderRadius: 4,
    cursor: "grab",
    touchAction: "none",
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <Group
      ref={setNodeRef}
      style={style}
      gap={6}
      wrap="nowrap"
      py={6}
      px={4}
      {...attributes}
      {...listeners}
    >
      <IconGridDots size={14} color="var(--mantine-color-gray-5)" />

      <Checkbox
        checked={checked}
        size="sm"
        onPointerDown={(e) => e.stopPropagation()}
        onChange={() => onToggle()}
      />

      <Text
        size="sm"
        style={{ flex: 1, cursor: "pointer", userSelect: "none" }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        {label}
      </Text>

      {tooltip && (
        <span onPointerDown={(e) => e.stopPropagation()}>
          <ColumnTooltip text={tooltip} />
        </span>
      )}

      <Group gap={2} wrap="nowrap">
        <ActionIcon
          variant="subtle"
          size="xs"
          disabled={index === 0}
          title="Move up"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onMoveUp();
          }}
        >
          <IconArrowUp size={13} />
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          size="xs"
          disabled={index === total - 1}
          title="Move down"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onMoveDown();
          }}
        >
          <IconArrowDown size={13} />
        </ActionIcon>
      </Group>
    </Group>
  );
}

interface IncomingPreferencesModalProps {
  opened: boolean;
  onClose: () => void;
  currentPreferences: IncomingQueuePreferences;
  onSave: (prefs: IncomingQueuePreferences) => void;
  isSaving: boolean;
}

export function IncomingPreferencesModal({
  opened,
  onClose,
  currentPreferences,
  onSave,
  isSaving,
}: IncomingPreferencesModalProps) {
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [visibleSet, setVisibleSet] = useState<Set<string>>(new Set());
  const [animatingId, setAnimatingId] = useState<string | null>(null);
  const [animatingDirection, setAnimatingDirection] = useState<
    "up" | "down" | null
  >(null);

  useEffect(() => {
    if (opened) {
      const visibleIds = currentPreferences.columns;
      const hiddenIds = ALL_INCOMING_COLUMNS.map((c) => c.id).filter(
        (id) => !visibleIds.includes(id)
      );
      setColumnOrder([...visibleIds, ...hiddenIds]);
      setVisibleSet(new Set(visibleIds));
    }
  }, [opened, currentPreferences]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setColumnOrder((prev) => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf(over.id as string);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const toggleColumn = (id: string) => {
    setVisibleSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const animateAndMove = (
    id: string,
    direction: "up" | "down",
    index: number
  ) => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === columnOrder.length - 1) return;

    setAnimatingId(id);
    setAnimatingDirection(direction);

    setTimeout(() => {
      setColumnOrder((prev) => {
        const next = [...prev];
        const target = direction === "up" ? index - 1 : index + 1;
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      });
      setAnimatingId(null);
      setAnimatingDirection(null);
    }, 160);
  };

  const handleSave = () => {
    const orderedVisible = columnOrder.filter((id) => visibleSet.has(id));
    onSave({ columns: orderedVisible });
  };

  const labelMap = Object.fromEntries(
    ALL_INCOMING_COLUMNS.map((c) => [c.id, c.label])
  );
  const tooltipMap = Object.fromEntries(
    ALL_INCOMING_COLUMNS.map((c) => [c.id, c.tooltip])
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Incoming Queue Preferences"
      size="md"
      closeOnClickOutside={false}
      closeOnEscape={false}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "75vh",
          padding: 0,
        },
      }}
    >
      <Stack
        gap="md"
        style={{ flex: 1, overflowY: "auto", padding: "16px 16px 0" }}
      >
        <div>
          <Text fw={600} size="sm" mb={4}>
            INCOMING COLUMNS
          </Text>
          <Text size="xs" c="dimmed" mb="sm">
            Drag to reorder or use the arrows. Toggle to show/hide.
          </Text>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={columnOrder}
              strategy={verticalListSortingStrategy}
            >
              <Stack gap={0}>
                {columnOrder.map((id, index) => (
                  <SortableRow
                    key={id}
                    id={id}
                    label={labelMap[id] ?? id}
                    tooltip={tooltipMap[id]}
                    checked={visibleSet.has(id)}
                    index={index}
                    total={columnOrder.length}
                    animatingId={animatingId}
                    animatingDirection={animatingDirection}
                    onToggle={() => toggleColumn(id)}
                    onMoveUp={() => animateAndMove(id, "up", index)}
                    onMoveDown={() => animateAndMove(id, "down", index)}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </div>
      </Stack>

      <Group
        justify="space-between"
        p="md"
        style={{
          borderTop: "1px solid var(--mantine-color-gray-2)",
          flexShrink: 0,
        }}
      >
        <Button variant="subtle" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} loading={isSaving}>
          Save Preferences
        </Button>
      </Group>
    </Modal>
  );
}
